import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Eye, EyeOff, RotateCcw, CheckCheck } from 'lucide-react';
import {
    getNotifications,
    markNotificationAsRead,
    markNotificationAsUnread,
    markAllNotificationsAsRead
} from '../../services/usersApi';
import Spinner from '../../components/Spinner/Spinner'; // Ajusta la ruta según tu estructura
import './UserNotifications.css';
import {useNotifications} from "../../utils/NotificationContext";

const UserNotifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastDocumentId, setLastDocumentId] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
    const navigate = useNavigate();
    const { markAllUnseenAsSeen } = useNotifications(); // Obtener la función del contexto

    useEffect(() => {
        loadNotifications();
        // Marcar todas las notificaciones no vistas como vistas cuando se carga la página
        markAllUnseenAsSeen();
    }, []);

    const loadNotifications = async (isLoadMore = false) => {
        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const docId = isLoadMore ? lastDocumentId : null;
            const pageSize = 10; // Número de notificaciones por carga
            const response = await getNotifications(pageSize, docId);

            if (isLoadMore) {
                setNotifications(prev => [...prev, ...response]);
            } else {
                setNotifications(response);
            }

            // Si recibimos menos de 10 notificaciones, asumimos que no hay más
            if (response.length < 10) {
                setHasMore(false);
            } else {
                // El último ID para paginación
                setLastDocumentId(response[response.length - 1].id);
            }

            setError(null);
        } catch (err) {
            console.error('Error loading notifications:', err);
            setError('Error al cargar las notificaciones');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleNotificationClick = async (notification) => {
        try {
            // Marcar como leída si no lo está
            if (!notification.read) {
                await markNotificationAsRead(notification.id);
                // Actualizar el estado local
                setNotifications(prev =>
                    prev.map(n =>
                        n.id === notification.id
                            ? { ...n, read: true }
                            : n
                    )
                );
            }

            // Navegar a la ruta especificada
            if (notification.redirectPath) {
                navigate(notification.redirectPath);
            }
        } catch (err) {
            console.error('Error marking notification as read:', err);
        }
    };

    const toggleReadStatus = async (notification, event) => {
        event.stopPropagation(); // Prevenir la navegación

        try {
            if (notification.read) {
                await markNotificationAsUnread(notification.id);
            } else {
                await markNotificationAsRead(notification.id);
            }

            // Actualizar el estado local
            setNotifications(prev =>
                prev.map(n =>
                    n.id === notification.id
                        ? { ...n, read: !n.read }
                        : n
                )
            );
        } catch (err) {
            console.error('Error toggling notification status:', err);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            setMarkingAllAsRead(true);
            await markAllNotificationsAsRead();

            // Actualizar el estado local - marcar todas como leídas
            setNotifications(prev =>
                prev.map(n => ({ ...n, read: true }))
            );
        } catch (err) {
            console.error('Error marking all notifications as read:', err);
        } finally {
            setMarkingAllAsRead(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp.seconds * 1000);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);

        if (diffInHours < 1) {
            return 'Hace unos minutos';
        } else if (diffInHours < 24) {
            return `Hace ${Math.floor(diffInHours)} hora${Math.floor(diffInHours) > 1 ? 's' : ''}`;
        } else {
            const diffInDays = Math.floor(diffInHours / 24);
            if (diffInDays < 7) {
                return `Hace ${diffInDays} día${diffInDays > 1 ? 's' : ''}`;
            } else {
                return date.toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            }
        }
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    if (loading) {
        return (
            <div className="notifications-container">
                <div className="notifications-header">
                    <h2>Notificaciones</h2>
                </div>
                <div className="loading-container">
                    <Spinner />
                    <span>Cargando notificaciones...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="notifications-container">
                <div className="notifications-header">
                    <h2>Notificaciones</h2>
                </div>
                <div className="error-container">
                    <p>{error}</p>
                    <button
                        className="btn-retry"
                        onClick={() => loadNotifications()}
                    >
                        <RotateCcw size={16} />
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="notifications-container">
            <div className="notifications-header">
                <h2>Notificaciones</h2>
                <div className="header-actions">
                    {unreadCount > 0 && (
                        <>
                            <button
                                className="btn-mark-all-read"
                                onClick={handleMarkAllAsRead}
                                disabled={markingAllAsRead}
                                title="Marcar todas como leídas"
                            >
                                {markingAllAsRead ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <div style={{fontSize:"12px"}}><CheckCheck size={16} strokeWidth={1.5}/>  Marcar todas como leídas</div>
                                )}
                            </button>
                            <div className="unread-badge">
                                {unreadCount}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {notifications.length === 0 ? (
                <div className="empty-state">
                    <Bell size={48} strokeWidth={1} />
                    <p>No tenés notificaciones</p>
                </div>
            ) : (
                <>
                    <div className="notifications-list">
                        {notifications.map((notification) => (
                            <div
                                key={notification.id}
                                className={`notification-item ${!notification.read ? 'unread' : ''}`}
                                onClick={() => handleNotificationClick(notification)}
                            >
                                <div className="notification-content">
                                    <div className="notification-header">
                                        <h3 className="notification-title">
                                            {notification.title}
                                        </h3>
                                        <button
                                            className="read-toggle"
                                            onClick={(e) => toggleReadStatus(notification, e)}
                                            title={notification.read ? 'Marcar como no leída' : 'Marcar como leída'}
                                        >
                                            {notification.read ?
                                                <EyeOff size={16} strokeWidth={1.5} /> :
                                                <Eye size={16} strokeWidth={1.5} />
                                            }
                                        </button>
                                    </div>
                                    <p className="notification-body" style={{ whiteSpace: 'pre-wrap' }}>
                                        {notification.body}
                                    </p>
                                    <div className="notification-footer">
                                        <span className="notification-time">
                                            {formatTimestamp(notification.timestamp)}
                                        </span>
                                        {!notification.read && (
                                            <div className="unread-dot" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {hasMore && (
                        <div className="load-more-container">
                            <button
                                className="btn-load-more"
                                onClick={() => loadNotifications(true)}
                                disabled={loadingMore}
                            >
                                {loadingMore ? (
                                    <>
                                        <Spinner size="sm" />
                                        Cargando...
                                    </>
                                ) : (
                                    'Cargar más'
                                )}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default UserNotifications;